import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import white from '../../assets/icons/popup/white-apple.svg?react';
import Logo from '../../assets/icons/popup/e-lotto-pcso-logo 1.svg';
import BgLatto from '../../assets/images/bg-latto.jpg';
import BgPattern from '../../assets/images/bg-pattern.png';
import { Download, Printer, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { gamesApi } from '../../api/games';
import { userApi } from '../../api/user';
import getGameImage from '../../utils/getGameImage';
import getGameType from '../../utils/games';
import * as htmlToImage from 'html-to-image';
import toast, { Toaster } from 'react-hot-toast';
import TicketTemplate1 from '../../ticket-templates/template-1';
import TicketTemplate2 from '../../ticket-templates/template-2';
import BgPatternBase64 from '../../assets/images/bg-pattern-base64';

const ticketTemplates = {
  1: TicketTemplate1,
  2: TicketTemplate2,
};

export default function TicketPopup({ isOpen, onClose, transaction }) {
  const [isVisible, setIsVisible] = useState(isOpen);
  const [isAnimating, setIsAnimating] = useState(false);
  const [games, setGames] = useState([]);
  const [groupedBets, setGroupedBets] = useState({});
  const { tokens, user } = useAuth();
  const [ticketDisplayName, setTicketDisplayName] = useState('');
  const [hasPrinter, setHasPrinter] = useState(true);
  const [betsLoaded, setBetsLoaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMode, setDownloadMode] = useState(false);
  const tableScrollRef = useRef(null);
  const [userTicketTemplate, setUserTicketTemplate] = useState(1);

  const TicketTemplate = useMemo(() => {
    return ticketTemplates[userTicketTemplate];
  }, [userTicketTemplate]);

  // get user game in session storage
  const userGame = sessionStorage.getItem('user_games');
  const userGames = JSON.parse(userGame);

  const printTicket = async () => {
    if (!hasPrinter) {
      return;
    }

    const tickets = document.querySelectorAll('.tickets');
    if (tickets.length > 1) {
      tickets.forEach(async (ticket) => {
        const dataUrl = await htmlToImage.toJpeg(ticket, {
          quality: 0.95,
        });
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              data_img: dataUrl,
              printerWidth: userTicketTemplate === 2 ? 550 : null,
            })
          );
        }
      });
      return;
    }

    htmlToImage
      .toJpeg(document.getElementById('print-ticket'), { quality: 0.95 })
      .then((dataUrl) => {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              data_img: dataUrl,
              printerWidth: userTicketTemplate === 2 ? 550 : null,
            })
          );
        }
      });
  };

  useEffect(() => {
    if (window.ReactNativeWebView) {
      setHasPrinter(true);
    }
    const fetchGames = async () => {
      try {
        // Fetch all games to get the image mapping
        const allGames = await gamesApi.getAllGames(tokens.access.token);
        const gameSettings = allGames.find(
          (game) => game.id === transaction?.gameSettingsId
        );
        const image = gameSettings?.otherSettings?.image;
        const gameType = getGameType(image);
        setGames({ gameType });

        // Group bets by game for multi-game transactions
        if (transaction?.bets) {
          const betsByGame = {};
          transaction.bets.forEach((bet) => {
            if (!betsByGame[bet.gameId]) {
              // Find game details from allGames
              const game = allGames.find((g) => g.id === bet.gameId);
              betsByGame[bet.gameId] = {
                bets: [],
                gameName: game?.name || `Game ${bet.gameId}`,
                gameType: getGameType(game?.otherSettings?.image) || '',
              };
            }
            betsByGame[bet.gameId].bets.push(bet);
          });
          setGroupedBets(betsByGame);
        }
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    if (transaction) {
      setTimeout(() => {
        printTicket();
      }, 1000);
      fetchGames();
    }
  }, [tokens.access.token, transaction]);

  useEffect(() => {
    const fetchTicketDisplayName = async () => {
      const response = await userApi.getUserById(tokens.access.token, user.id);
      setUserTicketTemplate(
        response.data.user.UserTicketTemplate?.templateId || 1
      );
      const threeDigitsFromMobileNumber =
        response.data.user.mobileNumber.slice(-3);
      if (response.data.user.ticketDisplayName) {
        setTicketDisplayName(response.data.user.ticketDisplayName);
      } else {
        setTicketDisplayName(threeDigitsFromMobileNumber);
      }
    };
    fetchTicketDisplayName();
  }, [tokens.access.token, user.id]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, transaction]);

  useEffect(() => {
    if (transaction?.bets) {
      setGroupedBets(groupBetsByGameSetting(transaction.bets));
    }
  }, [transaction]);

  useEffect(() => {
    if (transaction?.bets && transaction.bets.length > 0) {
      setBetsLoaded(true);
    } else {
      setBetsLoaded(false);
    }
  }, [transaction]);

  if (!isVisible) return null;

  const formatPeso = () => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(transaction.totalAmount);
  };

  const formatDrawTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours));
    date.setMinutes(parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const currentDate = new Date(transaction.drawDate).toLocaleDateString(
    'en-PH',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );
  const currentTime = new Date(transaction.drawDate).toLocaleTimeString(
    'en-PH',
    {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }
  );
  const getDrawTimeDisplay = () => {
    if (!transaction?.bets || transaction.bets.length === 0) return '';
    const allDrawTimes = transaction.bets.map((bet) =>
      bet.drawTime ? bet.drawTime.slice(0, 5) : ''
    );
    const firstDrawTime = allDrawTimes[0];
    const allSame = allDrawTimes.every((dt) => dt === firstDrawTime);
    return allSame ? formatDrawTime(firstDrawTime) : 'Multiple Draws';
  };

  const drawTime = getDrawTimeDisplay();

  const formatValue = (value) => {
    return value.toFixed(2).replace(/\.00$/, '');
  };

  function groupBetsByGameSetting(bets) {
    const grouped = {};
    bets.forEach((bet) => {
      const key = bet.gameSettingsId;
      if (!grouped[key]) {
        grouped[key] = {
          gameName: bet.GameSetting?.name || `Game ${key}`,
          bets: [],
          gameType: bet.GameSetting?.otherSettings?.image || '',
        };
      }
      grouped[key].bets.push(bet);
    });
    return grouped;
  }

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <Toaster position='top-center' />
      <div
        className={`relative w-full max-w-sm rounded-3xl overflow-hidden transform transition-all duration-300  ease-in-out ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          maxHeight: 'calc(100vh - 2rem)', // or adjust as needed
          overflowY: 'auto',
        }}
      >
        {/* Background with gradient and floating elements */}
        <div
          className='absolute inset-0'
          style={{
            backgroundImage: `url('${BgLatto}'), linear-gradient(132.14deg, #0279F9 0%, #293CA8 40%, #D42F55 100%)`,
            backgroundBlendMode: 'soft-light', // Ensures the gradient and image blend nicely
            backgroundSize: 'cover', // Ensures the image covers the card
            backgroundPosition: 'center', // Centers the image
          }}
        ></div>

        {/* Content */}
        <div className='relative text-center'>
          {/* Close button */}
          <button
            onClick={onClose}
            className='absolute z-50 right-4 top-4 text-white/80 hover:text-white transition-colors'
            aria-label='Close'
          >
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>

          {/* Logo */}
          <div className='flex justify-center text-white'>
            <h3 className='text-3xl font-semibold my-5 max-xs:my-3'>
              Bet Ticket
            </h3>
          </div>

          {/* Ticket section start */}

          <div
            id='ticket-section'
            className={`rounded-3xl px-6 py-4 max-xs:py-3 relative overflow-hidden font-mono bg-white ${
              downloadMode ? 'download-mode' : ''
            }`}
            style={{
              backgroundImage: !downloadMode
                ? `url('${BgPatternBase64}'), linear-gradient(132.14deg, #0279F9 0%, #293CA8 40%, #D42F55 100%)`
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {downloadMode && (
              <img
                src={BgPatternBase64}
                alt=''
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 0,
                  pointerEvents: 'none',
                  opacity: 1,
                }}
              />
            )}
            {/* Content */}
            <div className='relative'>
              {/* Header */}
              <div className='flex flex-col items-center'>
                <img
                  src={Logo}
                  alt='Lottery Logo'
                  className=' mx-3 mb-2 max-w-[98px]'
                />
                <div className='text-lg font-bold max-xs:text-base text-center mb-2'>
                  {transaction.gameName}
                  {Object.keys(groupedBets).length > 1 && (
                    <div className='text-sm font-normal text-blue-600'>
                      Multi-Game Transaction ({Object.keys(groupedBets).length}{' '}
                      games)
                    </div>
                  )}
                </div>
              </div>

              {/* Game Info */}
              <div className='flex items-center justify-between gap-3 mb-4'>
                <div className='flex items-center gap-2'>
                  {games.gameType && (
                    <img
                      src={getGameImage(games.gameType)}
                      className='w-9 h-9 max-xs:w-10 max-xs:h-10 max-xs:text-xs'
                    />
                  )}
                  <span className='font-normal text-base'>
                    {transaction?.gameName}
                  </span>
                </div>

                <div className='text-sm font-normal text-right max-xs:text-xs'>
                  {currentDate} <br />
                  {currentTime}
                </div>
              </div>

              {/* Ticket Number */}
              <div className='border border-black rounded-md p-2 font-normal '>
                <div className='text-sm  text-black mb-1'>Ticket #:</div>
                <div className=' text-sm max-xs:text-xs'>{transaction.id}</div>
              </div>

              {/* Date and Draw Time */}
              <div className='my-5 text-left max-xs:my-3 '>
                <div className='text-sm max-xs:text-xs'>
                  Date: {currentDate}
                </div>
                <div className='text-sm max-xs:text-xs'>Draw: {drawTime}</div>
              </div>

              <div className='flex justify-between items-center mb-3'>
                {/* Combinations */}
                <div className='w-full'>
                  <div className='text-sm mb-0.5 text-left max-xs:text-xs'>
                    Combinations
                  </div>
                  {/* Group all bets into a single table as per new design */}
                  {(() => {
                    if (!transaction?.bets) return null;
                    // Sort bets by drawTime, then by game
                    const sortedBets = [...transaction.bets].sort((a, b) => {
                      if (a.drawTime !== b.drawTime)
                        return a.drawTime.localeCompare(b.drawTime);
                      const gameA = a.GameSetting?.name || a.gameName || '';
                      const gameB = b.GameSetting?.name || b.gameName || '';
                      return gameA.localeCompare(gameB);
                    });
                    return (
                      <div
                        ref={tableScrollRef}
                        className='w-[100%] overflow-x-auto'
                      >
                        <table className='w-full text-xs mb-2 border border-black mt-2'>
                          <thead>
                            <tr>
                              <th className='py-1 px-2 border border-black'>
                                Combi
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Game
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Type
                              </th>
                              <th className='py-1 px-2 border border-black'>
                                Amount
                              </th>
                              {transaction.showWin && (
                                <th className='py-1 px-2 border border-black'>
                                  Winnings
                                </th>
                              )}
                              <th className='py-1 px-2 border border-black'>
                                Draw
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedBets.map((bet, index) => (
                              <tr key={index} className='text-center'>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.combination}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.GameSetting?.name || bet.gameName || ''}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.bettingOption === 'ramble'
                                    ? 'R'
                                    : bet.bettingOption === 'S/O'
                                    ? 'S/O'
                                    : 'S'}
                                </td>
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {bet.amount}
                                </td>
                                {transaction.showWin && (
                                  <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                    {bet.bettingOption === 'S/O'
                                      ? 'S/O'
                                      : Number(bet.winAmount).toLocaleString(
                                          'en-US',
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }
                                        )}
                                  </td>
                                )}
                                <td className='whitespace-nowrap py-1 px-2 border border-black'>
                                  {formatDrawTime(bet.drawTime)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Total Amount */}
              <div className='text-lg max-xs:text-base font-semibold mb-2.5'>
                Total Amount Paid: {formatPeso()}
              </div>

              {/* ticket display name from the user*/}
              <div className='text-sm max-xs:text-xs text-right'>
                {ticketDisplayName}
              </div>

              {/* QR Code */}
              <div className='w-[110px] mx-auto'>
                <QRCodeSVG value={transaction.id} size={110} />
              </div>

              {/* Total Amount */}
              <div className='text-lg font-semibold max-xs:text-base'>
                <Barcode value={transaction.id} className='rounded-lg w-full' />
              </div>
            </div>
          </div>

          {/* Ticket section end */}

          {/* Text */}

          <button
            className=' bg-[#0E64D1] w-2/3 hover:bg-blue-600 text-white p-2.5 max-xs:py-2 rounded-md transition-colors flex items-center justify-center gap-2.5 mx-auto my-2.5 max-xs:my-1 max-xs:text-sm '
            disabled={!betsLoaded || isDownloading}
            onClick={async () => {
              const ticketSection = document.getElementById('ticket-section');
              const scrollDiv = tableScrollRef.current;
              if (ticketSection) {
                setIsDownloading(true);
                setDownloadMode(true);

                // Save previous styles
                const prevTicketWidth = ticketSection.style.width;
                const prevTicketMaxWidth = ticketSection.style.maxWidth;
                const prevScrollOverflow = scrollDiv
                  ? scrollDiv.style.overflowX
                  : '';
                const prevScrollWidth = scrollDiv ? scrollDiv.style.width : '';

                // Expand ticket and table for download
                ticketSection.style.width = 'auto';
                ticketSection.style.maxWidth = 'none';
                if (scrollDiv) {
                  scrollDiv.style.overflowX = 'visible';
                  scrollDiv.style.width = 'auto';
                }

                setTimeout(() => {
                  htmlToImage
                    .toPng(ticketSection)
                    .then((dataUrl) => {
                      setDownloadMode(false);
                      // Revert styles
                      ticketSection.style.width = prevTicketWidth;
                      ticketSection.style.maxWidth = prevTicketMaxWidth;
                      if (scrollDiv) {
                        scrollDiv.style.overflowX = prevScrollOverflow;
                        scrollDiv.style.width = prevScrollWidth;
                      }
                      const link = document.createElement('a');
                      link.href = dataUrl;
                      link.download = `ticket-${transaction.id}.png`;
                      link.click();
                      setIsDownloading(false);
                    })
                    .catch(() => {
                      setDownloadMode(false);
                      // Revert styles on error
                      ticketSection.style.width = prevTicketWidth;
                      ticketSection.style.maxWidth = prevTicketMaxWidth;
                      if (scrollDiv) {
                        scrollDiv.style.overflowX = prevScrollOverflow;
                        scrollDiv.style.width = prevScrollWidth;
                      }
                      setIsDownloading(false);
                    });
                }, 500);
              }
            }}
          >
            {isDownloading ? (
              <>
                <Loader2 className='animate-spin' /> Downloading...
              </>
            ) : (
              <>
                Download Ticket
                <Download />
              </>
            )}
          </button>

          {hasPrinter && (
            <button
              className=' bg-[#0E64D1] w-2/3 hover:bg-blue-600 text-white p-2.5 max-xs:py-2 rounded-md transition-colors flex items-center justify-center gap-2.5 mx-auto my-2.5 max-xs:my-1 max-xs:text-sm '
              onClick={printTicket}
            >
              Print Ticket
              <Printer />
            </button>
          )}

          {/* Buttons */}
          <div className='flex justify-between gap-2 w-2/3 mx-auto text-base max-xs:text-sm mb-7 max-xs:mb-3 '>
            <button
              className='flex-1 bg-[#0E64D1] text-white p-2.5 max-xs:p-2 rounded-md transition-colors'
              onClick={() => {
                const smsUrl = `sms:?body=${encodeURI(text())}`;
                window.location.href = smsUrl;
              }}
            >
              Send as Text
            </button>
            <button
              className='flex-1 bg-[#0E64D1] border border-blue-600 text-white p-2.5 max-xs:p-2 rounded-md transition-colors'
              onClick={copyText}
            >
              Copy Text
            </button>
          </div>
          <TicketTemplate
            transaction={transaction}
            displayName={ticketDisplayName}
          />
        </div>
      </div>
    </div>
  );

  function toCurrency(value) {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  function text() {
    // get game name by game id
    let gameName = userGames.find(
      (game) => game.id === transaction.gameId
    )?.name;

    // If we have grouped bets and no game name, use the first game's name
    if (!transaction.gameName && Object.keys(groupedBets).length > 0) {
      const firstGameId = Object.keys(groupedBets)[0];
      gameName = groupedBets[firstGameId]?.gameName || gameName;
    }

    let text = `Game: ${gameName}\n`;
    text += `Ticket: ${transaction.id}\n`;
    text += `Date: ${currentDate}\n`;
    text += `Draw: ${drawTime}\n`;
    text += 'Combinations:\n';

    if (Object.keys(groupedBets).length > 1) {
      // Multi-game transaction
      Object.entries(groupedBets).forEach(([gameId, gameData]) => {
        text += `\n${gameData.gameName}:\n`;
        gameData.bets.forEach((bet) => {
          const type =
            bet.bettingOption === 'ramble'
              ? 'R'
              : bet.bettingOption === 'S/O'
              ? 'S/O'
              : 'S';
          text += `${bet.combination} (${type}) = ${toCurrency(bet.amount)}\n`;
        });
      });
    } else {
      // Single game transaction
      transaction.bets?.forEach((bet) => {
        const type =
          bet.bettingOption === 'ramble'
            ? 'R'
            : bet.bettingOption === 'S/O'
            ? 'S/O'
            : 'S';
        text += `${bet.combination} (${type}) = ${toCurrency(bet.amount)}\n`;
      });
    }

    text += `\nTotal Amount: ${toCurrency(transaction.totalAmount)}`;
    return text;
  }

  function copyText() {
    navigator.clipboard.writeText(text());
    toast.success('Text copied to clipboard');
  }
}
